import { makeStyles, shorthands, tokens } from "@fluentui/react-components";

export const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    backgroundColor: tokens.colorNeutralBackground1,
    // Page shell must be able to grow past one viewport: a fixed height clamps
    // the box while content overflows, letting the shared footer overlap it.
    minHeight: "100vh",
    // Prefer the dynamic viewport unit when supported so mobile browser chrome
    // doesn't add a phantom scroll. Falls back transparently to 100vh on
    // Safari < 15.4, Chromium < 108, Firefox < 101.
    "@supports (min-height: 100dvh)": {
      minHeight: "100dvh",
    },
  },
  subcontainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    justifyContent: "flex-start",
    "@media (min-width: 500px) and (max-width: 899px)": {
      marginLeft: "40px",
      marginRight: "40px",
      maxWidth: "100%",
    },
    "@media (min-width: 900px) and (max-width: 1239px)": {
      ...shorthands.margin("0px", "auto"),
      width: "840px",
    },
    "@media (min-width: 1240px) and (max-width: 1399px)": {
      marginLeft: "200px",
      marginRight: "200px",
      maxWidth: "100%",
    },
    "@media (min-width: 1400px)": {
      ...shorthands.margin("0px", "auto"),
      width: "1040px",
    },
  },
  main: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    alignItems: "stretch",
    ...shorthands.padding("8px", "16px", "16px", "16px"), // Reduced top padding
    ...shorthands.gap("6px"),
    backgroundColor: tokens.colorNeutralBackground1,
    "@media (min-width: 500px)": {
      ...shorthands.padding("0px", "0px", "20px", "0px"),
      // Fill the shell rather than measuring against it: a percentage height
      // resolves to `auto` under a min-height parent, so the region would
      // collapse to its content instead of stretching.
      flexGrow: 1,
      alignItems: "stretch",
      backgroundColor: tokens.colorNeutralBackground2,
    },
  },
  subMainContainer: {
    display: "flex",
    flexDirection: "column",
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow2,
    ...shorthands.borderRadius("8px"),
    ...shorthands.padding("12px"),
    "@media (min-width: 500px)": {
      // Absorb the leftover space instead of pinning to the viewport. The card
      // scrolls internally, so it must be free to shrink below its content
      // height -- hence the explicit minHeight reset for the flex item.
      flexGrow: 1,
      minHeight: 0,
      columnGap: "12px",
      flexDirection: "row",
      ...shorthands.padding("16px", "16px", "24px", "16px"),
      ...shorthands.overflow("hidden"),
      scrollbarGutter: "stable",
      "::-webkit-scrollbar": {
        width: "4px",
        height: "4px",
      },
      "::-webkit-scrollbar-thumb": {
        backgroundColor: "#888",
        ...shorthands.borderRadius("4px"),
      },
      "::-webkit-scrollbar-track": {
        backgroundColor: "#ccc",
        ...shorthands.borderRadius("4px"),
      },
      "::-webkit-scrollbar-thumb:hover": {
        backgroundColor: "#888",
      },
      "&:hover": {
        overflowY: "auto",
      },
    },
    "@media (min-width: 500px) and (max-width: 899px)": {
      marginLeft: "40px",
      marginRight: "40px",
      maxWidth: "100%",
    },
    "@media (min-width: 900px) and (max-width: 1239px)": {
      ...shorthands.margin("0px", "auto"),
      width: "840px",
    },
    "@media (min-width: 1240px) and (max-width: 1399px)": {
      marginLeft: "200px",
      marginRight: "200px",
      maxWidth: "100%",
    },
    "@media (min-width: 1400px)": {
      ...shorthands.margin("0px", "auto"),
      width: "1040px",
    },
  },
  card: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    justifyContent: "flex-start",
    ...shorthands.padding("12px"),
    ...shorthands.gap("12px"),
    ...shorthands.borderRadius("8px"),
    boxShadow: tokens.shadow2,
    backgroundColor: tokens.colorNeutralBackground1,
    "@media (min-width: 500px)": {
      flexDirection: "row",
    },
  },
  orderListContainer: {
    marginTop: "12px",
  },
  weekdayPickerContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "align-start",
    alignItems: "align-start",
    paddingLeft: "0px", // Remove left padding to align with page
    "@media (min-width: 500px)": {
      width: "100%",
      height: "auto",
      minHeight: "30px",
    },
  },
  weekdayRow: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingLeft: "0px", // Remove left padding to align with page
    width: "100%", // Ensure full width for proper spacing
  },
  orderButton: {
    fontSize: tokens.fontSizeBase400,
    color: tokens.colorBrandForeground1,
    ...shorthands.padding("0px", "0px", "0px", "0px"),
    width: "fitContent",
    minWidth: "none",
    ...shorthands.overflow("visible"),
    marginRight: "17px",
    marginTop: "17px",
  },
  bottomLinksContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    ...shorthands.padding("0px", "0px", "0px", "0px"),
    "@media (min-width: 500px) and (max-width: 899px)": {
      marginLeft: "40px",
      marginRight: "40px",
      maxWidth: "100%",
    },
    "@media (min-width: 900px) and (max-width: 1239px)": {
      ...shorthands.margin("0px", "auto"),
      width: "840px",
    },
    "@media (min-width: 1240px) and (max-width: 1399px)": {
      marginLeft: "200px",
      marginRight: "200px",
      maxWidth: "100%",
    },
    "@media (min-width: 1400px)": {
      ...shorthands.margin("0px", "auto"),
      width: "1040px",
    },
  },
  feedbackContainer: {
    display: "flex",
    alignItems: "center",
    "& .ocvFeedbackLaunchButton": {
      whiteSpace: "nowrap",
    },
  },
});
